/**
 * Les props de la salle d'attente (`<StudentLiveEntryPage>`, et `<TutorPreCallPage>` qui la reprend) que
 * l'élève et le masteur branchent de la même façon : l'image et sa caméra, la rangée du test, son geste, le
 * mot d'un appareil en défaut, l'étape dépliée, et le choix du navigateur. Ce qui diffère (la question, le
 * statut, l'entrée, le micro sur l'image de l'élève) reste à chaque écran.
 */
import React from "react"

import { LiveBrowserChoice } from "../../../../../../design-system/student"
import { type DeviceCheckController } from "../../../../../../engine/precall/useDeviceCheckMachine"
import { type DeviceCheckVM } from "../../../../view-models/student/LiveEntry/deviceCheckViewModel"
import { type LiveEntryEquipmentVM } from "../../../../view-models/student/LiveEntry/liveEntryLobbyViewModel"
import { StudentCameraFeed, StudentDeviceCheckStep } from "./StudentDeviceCheck"
import { type LiveEntryBrowser } from "./useLiveEntryParts"

/**
 * Le test du matériel posé dans la page. `testShown` faux : ni étape (la séance pas encore lue).
 * `releasing` : les appareils se rendent avant le live, l'image est démontée. `mic` : la bascule du micro
 * sur l'image (l'élève ; le masteur n'en a pas).
 */
export function liveEntryCheckProps(args: {
    equipment: LiveEntryEquipmentVM
    check: DeviceCheckController
    vm: DeviceCheckVM
    testShown: boolean
    releasing: boolean
    mic?: { on: boolean; toggle: () => void }
}) {
    const { equipment, check, vm, testShown, releasing, mic } = args
    const notice = equipment.notice
    return {
        preview: {
            micOn: mic?.on,
            onToggleMic: mic?.toggle,
            cameraOn: !vm.preview.videoOff,
            onToggleCamera: () => check.setVideoMuted(!vm.preview.videoOff),
            cameraProblem: equipment.cameraProblem,
            micProblem: equipment.micProblem,
            obstructed: vm.preview.obstructed,
            deviceError: vm.preview.deviceError,
            network: equipment.network,
            waitingText: equipment.cameraWaiting,
            children: equipment.feedMounted && !releasing ? <StudentCameraFeed check={check} /> : undefined,
        },
        lines: equipment.lines,
        checkNote: equipment.note,
        checkAction:
            equipment.action != null
                ? {
                      label: equipment.action.label,
                      onPress: equipment.action.kind === "start" ? check.start : check.recheck,
                  }
                : undefined,
        checkPending: equipment.pending,
        onRetryLine: equipment.retryable ? check.reopen : undefined,
        notice:
            notice != null
                ? {
                      text: notice.text,
                      tone: notice.tone,
                      actionLabel: notice.actionLabel,
                      onAction: () => check.reopen(notice.retry),
                  }
                : undefined,
        check: testShown ? <StudentDeviceCheckStep vm={vm} check={check} /> : undefined,
    }
}

/** Le choix du navigateur, à la place de toute la page ; rien quand il n'est pas à l'écran. */
export function liveEntryBrowserChoice(browser: LiveEntryBrowser | null, onContinueHere: () => void) {
    if (browser == null) return undefined
    return (
        <LiveBrowserChoice
            device={browser.device}
            appDomain={browser.appDomain}
            onPrimary={browser.onPrimary}
            onContinueHere={onContinueHere}
        />
    )
}
